import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

function auth() {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
}

// ===== Pedidos (telas operacionais) =====

/** Motivos de cancelamento válidos AGORA para um pedido do Núcleo que veio do iFood. */
export async function fetchCancelReasonsByPedido(pedidoId) {
  const { data } = await axios.get(`${API}/api/ifood/pedidos/${pedidoId}/cancellation-reasons`, auth());
  return data;
}

/** Mesma lista, pelo id do pedido no iFood. */
export async function fetchCancelReasonsByIfoodOrder(ifoodOrderId) {
  const { data } = await axios.get(`${API}/api/ifood/orders/${ifoodOrderId}/cancellation-reasons`, auth());
  return { ifood_order_id: ifoodOrderId, reasons: data.reasons || [] };
}

/** Negociações em aberto (cliente pediu cancelamento/reembolso/tempo). */
export async function fetchDisputes() {
  const { data } = await axios.get(`${API}/api/ifood/disputes`, auth());
  return data;
}

/** action: "accept" | "reject" | "alternative" */
export async function respondDispute(disputeId, action, body) {
  const { data } = await axios.post(`${API}/api/ifood/disputes/${disputeId}/${action}`, body || {}, auth());
  return data;
}

/** Código de entrega (entrega própria): o entregador pede ao cliente, o iFood valida.
 *  Sem login de propósito — a tela do entregador é um link público. */
export async function verifyDeliveryCode(pedidoId, code) {
  const { data } = await axios.post(`${API}/api/ifood/pedidos/${pedidoId}/verify-delivery-code`, { code }, auth());
  return data;
}

/** Entrega iFood: posição atual do entregador (404 = sem rastreio ainda). */
export async function fetchOrderTracking(pedidoId) {
  const { data } = await axios.get(`${API}/api/ifood/pedidos/${pedidoId}/tracking`, auth());
  return data;
}

/** Entrega iFood: a loja digita o código que o entregador informou na coleta. */
export async function validatePickupCode(pedidoId, code) {
  const { data } = await axios.post(`${API}/api/ifood/pedidos/${pedidoId}/validate-pickup-code`, { code }, auth());
  return data;
}

// ===== Conexão (Configurações → Sistema → Integração → iFood) =====

export async function fetchConnection() {
  const { data } = await axios.get(`${API}/api/ifood/connection`, auth());
  return data;
}

export async function connectMerchant(merchantId, label) {
  const { data } = await axios.post(`${API}/api/ifood/connect`, { merchant_id: merchantId, label }, auth());
  return data;
}

export async function disconnectMerchant(merchantId) {
  const { data } = await axios.delete(`${API}/api/ifood/merchants/${encodeURIComponent(merchantId)}`, auth());
  return data;
}

/** Cadastro da loja no iFood + disponibilidade por operação. */
export async function fetchMerchantDetails() {
  const { data } = await axios.get(`${API}/api/ifood/merchant/details`, auth());
  return data;
}

/** Turnos cadastrados no iFood, por dia da semana. */
export async function fetchIfoodOpeningHours() {
  const { data } = await axios.get(`${API}/api/ifood/merchant/opening-hours`, auth());
  return data;
}

/** Pausas ativas da loja no iFood. */
export async function fetchInterruptions() {
  const { data } = await axios.get(`${API}/api/ifood/merchant/interruptions`, auth());
  return data;
}

export async function removeInterruption(interruptionId) {
  const { data } = await axios.delete(
    `${API}/api/ifood/merchant/interruptions/${encodeURIComponent(interruptionId)}`, auth());
  return data;
}

// ===== Financeiro =====

/** Resumo do período: vendas, taxa de entrega, comissão estimada e líquido. */
export async function fetchFinancialSummary(params = {}) {
  const { data } = await axios.get(`${API}/api/ifood/financial/summary`, { ...auth(), params });
  return data;
}

/** Repasses do iFood. `disponivel: false` = o iFood ainda não liberou o módulo. */
export async function fetchSettlements(params = {}) {
  const { data } = await axios.get(`${API}/api/ifood/financial/settlements`, { ...auth(), params });
  return data;
}

/** Salva as taxas do contrato da loja com o iFood (%). */
export async function saveIfoodTaxas(taxas) {
  const { data } = await axios.put(`${API}/api/ifood/config`, taxas, auth());
  return data;
}

// ===== Cardápio =====

export async function fetchCatalogs() {
  const { data } = await axios.get(`${API}/api/ifood/catalog`, auth());
  return Array.isArray(data) ? data : [];
}

export async function fetchCatalogItems() {
  const { data } = await axios.get(`${API}/api/ifood/catalog/items`, auth());
  return Array.isArray(data) ? data : [];
}

export async function fetchMappings() {
  const { data } = await axios.get(`${API}/api/ifood/catalog/mappings`, auth());
  return Array.isArray(data) ? data : [];
}

export async function fetchProducts() {
  const { data } = await axios.get(`${API}/api/products`, auth());
  return Array.isArray(data) ? data : [];
}

export async function mapProduct(productId, body) {
  const { data } = await axios.post(`${API}/api/ifood/catalog/map/${productId}`, body, auth());
  return data;
}

export async function unmapProduct(productId) {
  const { data } = await axios.delete(`${API}/api/ifood/catalog/map/${productId}`, auth());
  return data;
}

export async function syncAvailability() {
  const { data } = await axios.post(`${API}/api/ifood/catalog/sync-availability`, {}, auth());
  return data;
}

/** externalCode = id do produto em todos os itens do iFood. */
export async function syncExternalCodes() {
  const { data } = await axios.post(`${API}/api/ifood/catalog/sync-external-codes`, {}, auth());
  return data;
}

// ===== Eventos =====

export async function fetchEventsLog(limit = 80) {
  const { data } = await axios.get(`${API}/api/ifood/events/log?limit=${limit}`, auth());
  // O backend devolve a lista direta; a tela antiga lia `data.events` e por isso
  // sempre mostrava "Nenhum evento" (bug herdado, visto na homologação 16/09).
  return Array.isArray(data) ? data : (data?.events || []);
}

/** Foto de produto: o backend serve `/uploads/...` atrás de `/api` (igual às outras telas). */
export function imageUrl(url) {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http")) return url;
  return url.startsWith("/") ? `/api${url}` : `/api/${url}`;
}

/** Mensagem de erro amigável a partir de um erro do axios. */
export function errorMessage(e, fallback = "Erro de conexão") {
  return e?.response?.data?.detail || e?.response?.data?.message || e?.message || fallback;
}
